import { clip, hiddenText, isSecretKey, redact, redactQuery, redactText, withoutKnownMask } from '../../shared/redact';
import { getBases } from './base';
import { appVersion } from './config';
import { piiSelector } from './elementText';
import { describeElement, recorded } from './recorder';

// Collected before the report dialog opens, so it describes the page itself.

const STORAGE_KEYS_MAX = 60;
const STORAGE_VALUE_MAX = 500;
const PAGE_HTML_MAX = 2_000_000;

/** A freshly issued token (settings of the widget or of the host): not in the snapshot, the HTML or the screenshot. */
export const SENSITIVE_SELECTOR = '.token-secret';

export { piiSelector };

function visible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

function texts(selector: string, max: number, limit = 120): string[] {
  return [...document.querySelectorAll(selector)]
    .filter(visible)
    .map((el) => {
      const text = clip((el.textContent ?? '').replace(/\s+/g, ' ').trim(), limit);
      return text && el.closest(piiSelector()) ? hiddenText(text) : redactText(text);
    })
    .filter(Boolean)
    .slice(0, max);
}

function storage(store: Storage | undefined): Record<string, string> | { error: string } {
  if (!store) return {};
  try {
    const out: Record<string, string> = {};
    for (let i = 0; i < Math.min(store.length, STORAGE_KEYS_MAX); i += 1) {
      const key = store.key(i);
      if (key !== null) out[key] = clip(store.getItem(key) ?? '', STORAGE_VALUE_MAX);
    }
    return redact(out);
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

function fields(): { field: string; value: string }[] {
  return [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select')]
    .filter((el) => visible(el) && !['password', 'hidden', 'file'].includes((el as HTMLInputElement).type))
    .filter((el) => !isSecretKey(el.name || el.getAttribute('autocomplete') || ''))
    .map((el) => ({
      field: describeElement(el),
      value:
        el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')
          ? String(el.checked)
          : el instanceof HTMLTextAreaElement
            ? hiddenText(clip(el.value, 300)) // a draft is a person's words: only its length
            : redactText(clip(el.value, 300)),
    }))
    .filter((item) => item.value !== '' && item.value !== 'false')
    .slice(0, 30);
}

function hiddenTextOrNull(text: string): string | null {
  const clean = text.trim();
  return clean ? hiddenText(clean) : null;
}

function buildAsset(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return script ? new URL(script.src, window.location.href).pathname : null;
}

export interface SnapshotContext {
  username: string;
  isSuper: boolean;
  /** Host fields of `app` (facts: `ui`). */
  app?: Record<string, unknown>;
}

/**
 * `again: false` — no dictionary pass here at all (report 74: it held the opening): neither the second one over the
 * recorded lists nor the first over the page's texts; `finishClientState` gives every string that pass later.
 */
export function collectClientState(ctx: SnapshotContext, options: { again?: boolean } = {}): Record<string, unknown> {
  return options.again === false ? withoutKnownMask(() => stateOf(ctx, options)) : stateOf(ctx, options);
}

function stateOf(ctx: SnapshotContext, options: { again?: boolean }): Record<string, unknown> {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string; rtt?: number } }).connection;
  const log = recorded(options);
  const basePath = getBases().host;
  return {
    schema: 1,
    capturedAt: new Date().toISOString(),
    // The build of this tab (configure appVersion): the service's app_version when the host proxy sends none.
    appVersion: appVersion(),
    app: {
      basePath,
      ...ctx.app,
      user: { username: ctx.username, isSuper: ctx.isSuper },
      buildAsset: buildAsset(),
      sinceLoadSec: Math.round(performance.now() / 1000),
    },
    page: {
      url: redactQuery(window.location.href),
      path: window.location.pathname.slice(basePath.length) || '/',
      search: redactQuery(window.location.search),
      hash: redactQuery(window.location.hash),
      title: redactText(document.title),
      headings: texts('h1, h2, h3', 15),
      openDialogs: texts('.modal h2, [role=dialog] h2, [role=dialog][aria-label]', 5),
      activeTabs: [...document.querySelectorAll('[aria-selected="true"], [aria-current="page"], [aria-pressed="true"]')]
        .filter(visible)
        .slice(0, 15)
        .map(describeElement),
      focused: document.activeElement && document.activeElement !== document.body ? describeElement(document.activeElement) : null,
      selection: hiddenTextOrNull(window.getSelection()?.toString() ?? ''),
      fields: fields(),
      errorsOnScreen: texts('.error-box, .notice-box, [role=alert]', 10, 300),
      scroll: {
        x: Math.round(window.scrollX),
        y: Math.round(window.scrollY),
        maxY: Math.max(0, document.documentElement.scrollHeight - window.innerHeight),
      },
    },
    env: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform,
      viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio },
      screen: { width: window.screen.width, height: window.screen.height },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
      darkScheme: pageIsDark(),
      systemDark: window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
      memoryMb: memory ? { used: Math.round(memory.usedJSHeapSize / 1048576), limit: Math.round(memory.jsHeapSizeLimit / 1048576) } : null,
      connection: connection ? { effectiveType: connection.effectiveType ?? null, rtt: connection.rtt ?? null } : null,
    },
    timing: nav
      ? { domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd), loadMs: Math.round(nav.loadEventEnd), type: nav.type }
      : null,
    network: log.network,
    failedNetworkEarlier: log.failedNetwork,
    console: log.console,
    actions: log.actions,
    storage: { local: storage(window.localStorage), session: storage(window.sessionStorage) },
  };
}

/**
 * The theme the page shows, never the system one on its own: a page drawn light on a dark system (or a host forced to
 * one theme) is that theme. Its own color-scheme first; a page allowing both follows the system, as the browser does;
 * a page without color-scheme — the theme attribute a host switch sets (`data-theme` on <html> or <body>), else light,
 * as the browser draws it.
 */
export function pageIsDark(): boolean {
  const scheme = getComputedStyle(document.documentElement).colorScheme ?? '';
  const dark = /\bdark\b/.test(scheme);
  const light = /\blight\b/.test(scheme);
  if (dark !== light) return dark;
  if (dark && light) return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  const theme = (document.documentElement.dataset.theme || document.body?.dataset.theme || '').toLowerCase();
  return theme === 'dark';
}

/** Field values are stripped here; the visible ones are already in the state as `page.fields`. */
function strip(root: Element): Element {
  root.querySelectorAll('script, style, link, noscript, iframe').forEach((el) => el.remove());
  root.querySelectorAll('input, textarea').forEach((el) => {
    el.removeAttribute('value');
    if (el instanceof HTMLTextAreaElement) el.textContent = '';
  });
  root.querySelectorAll(`.bug-report-overlay, [data-bug-own], ${SENSITIVE_SELECTOR}`).forEach((el) => el.remove());
  root.querySelectorAll(piiSelector()).forEach((el) => {
    const text = (el.textContent ?? '').trim();
    if (text) el.textContent = hiddenText(text);
  });
  return root;
}

/** The stripped page before the dictionary pass; a page over the cap — only its `<main>`. */
function strippedHtml(root: Element): string {
  const main = root.querySelector('main')?.cloneNode(true) as Element | undefined;
  const whole = strip(root).outerHTML;
  if (whole.length <= PAGE_HTML_MAX) return `<!doctype html>\n${whole}`;
  const part = main ? strip(main).outerHTML : whole;
  return clip(`<!-- страница больше ${PAGE_HTML_MAX} символов: только <main> -->\n${part}`, PAGE_HTML_MAX);
}

export function pageHtml(): string {
  return redactText(strippedHtml(document.documentElement.cloneNode(true) as Element));
}

/**
 * The page as it is at the moment of the report, uncleaned: only `outerHTML` (tens of ms), held in memory. The cleaning
 * — strip and the dictionary pass, ~0.5 s on a chat page — is `cleanPageHtml`, later and in pieces (report 74, П217).
 */
export function pageSource(): string {
  return document.documentElement.outerHTML;
}

/** Yields to the page between pieces of work. */
export type Pause = () => Promise<void>;
export const pauseTask: Pause = () => new Promise((resolve) => setTimeout(resolve, 0));

const PIECE = 24_000;

/**
 * `redactText` in pieces cut only between two tags (`><`): a text node or a tag never spans two pieces, and the
 * dictionary looks for names and quotes inside them.
 */
export async function redactInPieces(text: string, pause: Pause): Promise<string> {
  let out = '';
  let from = 0;
  while (from < text.length) {
    let to = from + PIECE;
    if (to >= text.length) to = text.length;
    else {
      const cut = text.indexOf('><', to);
      to = cut < 0 ? text.length : cut + 1;
    }
    out += redactText(text.slice(from, to));
    from = to;
    if (from < text.length) await pause();
  }
  return out;
}

export async function cleanPageHtml(source: string, pause: Pause = pauseTask): Promise<string> {
  const doc = new DOMParser().parseFromString(source, 'text/html');
  const html = strippedHtml(doc.documentElement);
  await pause();
  return redactInPieces(html, pause);
}

function redactDeep(value: unknown): unknown {
  if (typeof value === 'string') return redactText(value);
  if (Array.isArray(value)) return value.map(redactDeep);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactDeep(item)]));
  return value;
}

/**
 * The second dictionary pass over a state taken with `collectClientState(…, { again: false })`: every string, with the
 * dictionary as it is now; the long lists (requests with their bodies) one entry at a time.
 */
export async function finishClientState(state: Record<string, unknown>, pause: Pause = pauseTask): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (Array.isArray(value) && value.length > 8) {
      const list: unknown[] = [];
      for (const item of value) {
        list.push(redactDeep(item));
        if (list.length % 8 === 0) await pause();
      }
      out[key] = list;
    } else out[key] = redactDeep(value);
    await pause();
  }
  return out;
}
